import { Avatar, Button, Checkbox, Typography ,Tooltip} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import { isAdminUser, showUserDisplayName } from '../../../../lib/userUtils';
import { useProjectUsers } from '../../../../query/apiQueries';
import { formatCountryOptions } from '../../../../static-data/formatCountryOptions';
import SearchBar from '../../../elements/SearchBar';
import TextInput from '../../../elements/TextInput';
import TestCaseTable from './TestCaseTable';

const NewIterationForm = ({
    environments,
    loading,
    selectedEnvironment,
    setSelectedEnvironment,
    selectedTestCases,
    setSelectedTestCases,
    iterationName,
    setIterationName,
    testCycleCountries,
    selectedCountry,
    setSelectedCountry,
    mode,
    testCycleId,
    executions = [],
    fetchTestCases
}) => {

    const [showModal, setShowModal] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [tempSelectedIds, setTempSelectedIds] = useState([]);
    const { projectId } = useParams();
    const { data: users = [] } = useProjectUsers(projectId);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkTester, setBulkTester] = useState("");
    const [modalAssociations, setModalAssociations] = useState([]);
    const { addNotification } = useNotification();
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const isProjectAdmin = useMemo(() => {
        const role = users.find(u => u.id === currentUser?.id)?.role;
        return role === 'admin' || role === 'project-admin';
    }, [users, currentUser?.id]);

    useEffect(() => {
        if (mode === "edit" && executions.length > 0) {
            const mapped = executions.map(ex => ({
                testCaseAssociationId: ex.association.id,
                testCaseId: ex.test_case_id,
                testCaseKey: ex.association.testcase.key,
                summary: ex.association.testcase.summary,
                assignedTo: ex.executed_by ?? null,
                testSuites: ex.association.testcase?.test_suites || [],
                isEditable: ex.is_editable ?? false
            }));

            setSelectedTestCases(mapped);
            setTempSelectedIds(mapped.map(x => x.testCaseAssociationId));
        }
    }, [mode, executions]);

    const filteredSelected = useMemo(() => {
        if (!searchValue.trim()) return selectedTestCases;
        const q = searchValue.toLowerCase();
        return selectedTestCases.filter(tc =>
            tc.testCaseKey?.toLowerCase().includes(q) ||
            tc.summary?.toLowerCase().includes(q)
        );
    }, [searchValue, selectedTestCases]);

    const hasNonEditableExecutions = useMemo(() => {
        return selectedTestCases.some(tc => tc.isEditable === false);
    }, [selectedTestCases]);


    const applySelection = () => {
        if (mode === "edit") return;

        const finalList = tempSelectedIds
            .map(id => {
                const found = modalAssociations.find(x => x.id === id);
                if (!found) return null;

                return {
                    testCaseAssociationId: found.id,
                    testCaseId: found.testcase.id,
                    testCaseKey: found.testcase.key,
                    summary: found.testcase.summary,
                    assignedTo: found.assigned_to ?? null,
                    testSuites: found.testcase?.test_suites || []
                };
            })
            .filter(Boolean);

        setSelectedTestCases(finalList);
        setShowModal(false);
    };

    const assignTesterBulk = () => {
        if (!bulkTester || selectedRows.length === 0) return;

        const updated = selectedTestCases.map(tc => {
            if (selectedRows.includes(tc.testCaseAssociationId)) {
                const needsUpdate = tc.assignedTo !== bulkTester;
                return { ...tc, assignedTo: bulkTester, update: needsUpdate };
            }
            return tc;
        });

        setSelectedTestCases(updated);
        setSelectedRows([]);
        setBulkTester("");
    };


    const toggleRow = (id) => {
        setSelectedRows(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    const editableTestCaseIds = useMemo(
        () => selectedTestCases
            .filter(tc => tc.isEditable !== false)
            .map(tc => tc.testCaseAssociationId),
        [selectedTestCases]
    );

    const isAllSelected = editableTestCaseIds.length > 0 &&
        editableTestCaseIds.every(id => selectedRows.includes(id));

    const handleSelectTestCasesClick = () => {
        setShowModal(true);
    };

    const countryOptions = formatCountryOptions(testCycleCountries);
    const shouldShowCountry =
    mode !== 'edit'
        ? countryOptions.length > 0
        : selectedCountry &&
        countryOptions.some((item) => item.value === selectedCountry);

    return (
        <>
            <Form>

                <Form.Group className="mb-3">
                    <Form.Label className="required">Iteration Name</Form.Label>
                    <TextInput
                        required
                        value={iterationName || ''}
                        onChange={(id, v) => setIterationName(v)}
                        maxLength={100}
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="required">Environment</Form.Label>
                            <Form.Select
                                value={selectedEnvironment}
                                disabled={mode === 'edit'}
                                onChange={e => setSelectedEnvironment(e.target.value)}
                            >
                                <option value="">Select</option>
                                {environments.map(env => (
                                    <option key={env.id} value={env.id}>{env.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    {shouldShowCountry && (
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Country</Form.Label>
                                <Form.Select
                                    value={selectedCountry}
                                    disabled={mode === 'edit'}
                                    onChange={e => setSelectedCountry(e.target.value)}
                                >
                                    <option value="">Select</option>
                                    {countryOptions?.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {(() => {
                                                const flag = item.display.props.children[0];
                                                const code = flag.props.countryCode;
                                                const flagEmoji = code
                                                    .toUpperCase()
                                                    .replace(/./g, char => String.fromCodePoint(0x1F1E6 + char.charCodeAt() - 65));
                                                return `${flagEmoji} ${item.value}`;
                                            })()}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    )}
                </Row>

                <Form.Group className="mb-3">
                    <div className="mt-3">

                        <div className="my-3 d-flex align-items-center justify-content-between">
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Test Cases ({selectedTestCases.length})
                            </Typography>

                            {mode === "create" && (
                                <Button variant="contained" onClick={handleSelectTestCasesClick}>
                                    Select Test Cases
                                </Button>
                            )}
                        </div>
                        {selectedTestCases.length > 0 && (<>
                        <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                            {(isAdminUser() || isProjectAdmin) && <Tooltip title="Select all">
                            <span>
                                <Checkbox
                                checked={isAllSelected}
                                disabled={editableTestCaseIds.length === 0}
                                onChange={() => {
                                    if (isAllSelected) {
                                    setSelectedRows([]);
                                    } else {
                                    setSelectedRows(editableTestCaseIds);
                                    }
                                }}
                                />
                            </span>
                            </Tooltip>
                            }
                            <SearchBar
                                value={searchValue}
                                onChange={e => setSearchValue(e.target.value)}
                                clearSearch={() => setSearchValue('')}
                            />              
                               
                            {(isAdminUser() || isProjectAdmin) && <>
                                <Form.Select
                                    size="sm"
                                    style={{ width: "250px" }}
                                    value={bulkTester}
                                    onChange={(e) => setBulkTester(e.target.value)}
                                >
                                    <option value="">Assign Tester</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </option>
                                    ))}
                                </Form.Select>

                                <Button
                                    variant="contained"
                                    disabled={!bulkTester || selectedRows.length === 0}
                                    onClick={assignTesterBulk}
                                >
                                    Apply
                                </Button>                              
                            </>}
                        </div>

                        <div className="test-cases-container">
                            {loading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                    <Spinner animation="border" />
                                </div>
                            ) : selectedTestCases.length === 0 ? null : filteredSelected.length ? (
                                filteredSelected.map((tc, i) => (
                                    <div
                                        key={tc.testCaseAssociationId}
                                        className="mb-2 d-flex align-items-center gap-2"
                                        style={{ width: "100%" }}
                                    >
                                        <div style={{ width: 32 }}>
                                            {(isAdminUser() || isProjectAdmin) && (
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedRows.includes(tc.testCaseAssociationId)}
                                                    onChange={() => toggleRow(tc.testCaseAssociationId)}
                                                    disabled={tc.isEditable === false}
                                                    title={tc.isEditable ? 'Select to assign tester' : ''}
                                                />
                                            )}
                                        </div>

                                        <div
                                            className="flex-grow-1"
                                            style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}
                                        >
                                            <strong>#{i + 1}</strong> — {tc.testCaseKey}: {tc.summary.length > 80 ? tc.summary.slice(0, 80) + '…' : tc.summary}
                                        </div>


                                        <div
                                            className="d-flex align-items-center justify-content-center"
                                            style={{ width: 90, flexShrink: 0 }}
                                        >
                                            {tc.assignedTo ? (
                                                <Avatar
                                                    sx={{ width: 24, height: 24, fontSize: "0.8rem" }}
                                                    title={showUserDisplayName(tc.assignedTo, users)}
                                                >
                                                    {showUserDisplayName(tc.assignedTo, users)?.[0]}
                                                </Avatar>
                                            ) : (
                                                <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                                                    Unassigned
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted">No test cases.</p>
                            )}
                        </div>
                        {hasNonEditableExecutions && (
                            <div
                                className="mt-2"
                                style={{
                                    fontSize: '0.85rem',
                                    color: '#856404',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeeba',
                                    borderRadius: 4,
                                    padding: '8px 12px'
                                }}
                            >
                                <strong>Note:</strong> Some TCs in this iteration are non-editable because newer executions
                                were created for them in a different iteration with the same environment
                                and country combination.
                            </div>
                        )}
                    </>)}
                    </div>
                </Form.Group>
            </Form>

            {mode === "create" && showModal && (
                <TestCaseTable
                    entityId={testCycleId}
                    projectId={projectId}
                    addNotification={addNotification}
                    selectedTestCases={tempSelectedIds}
                    setSelectedTestCases={setTempSelectedIds}
                    show={showModal}
                    handleCloseButton={() => setShowModal(false)}
                    handleAddTestCases={applySelection}
                    setFetchedAssociations={setModalAssociations}
                />
            )}
        </>
    );
};

export default NewIterationForm;
